import { type FunctionComponent, useState } from "react";
import { FollowAction, FollowType } from "scmp-sdk";

import { useMynewsFeedViewContext } from "scmp-app/components/my-news/feed-view/context";
import { useUserService } from "scmp-app/lib/user-service/hooks";

import IconClose from "./icon-close.svg";
import IconFollow from "./icon-follow.svg";
import IconUnfollow from "./icon-unfollow.svg";
import {
  Button,
  ButtonContainer,
  Container,
  Header,
  ItemIcon,
  ItemName,
  ItemType,
  List,
  ListItem,
  Title,
} from "./styles";

type Props = {
  className?: string;
  onClose?: () => void;
};
export const TopicFilteringPopup: FunctionComponent<Props> = ({ className, onClose }) => {
  const { followedList } = useMynewsFeedViewContext();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { followAction } = useUserService();

  const handleItemClick = (entityId: string) => {
    setSelectedItems(previousSelectedItems =>
      previousSelectedItems.includes(entityId)
        ? previousSelectedItems.filter(id => id !== entityId)
        : [...previousSelectedItems, entityId],
    );
  };

  const onConfirm = async () => {
    try {
      const itemsMap = new Map(followedList.map(item => [item.entityId, item]));

      const grouped = selectedItems.reduce(
        (accumulator, itemId) => {
          const item = itemsMap.get(itemId);
          if (item) {
            switch (item.__typename) {
              case "Author":
                accumulator.authorsId.push(item.entityId);
                break;
              case "Section":
                accumulator.sectionsId.push(item.entityId);
                break;
              case "Topic":
                accumulator.topicsId.push(item.entityId);
                break;
            }
          }
          return accumulator;
        },
        { authorsId: [] as string[], sectionsId: [] as string[], topicsId: [] as string[] },
      );

      await Promise.all([
        grouped.authorsId.length > 0 &&
          followAction(FollowAction.UnFollow, FollowType.Author, grouped.authorsId),
        grouped.sectionsId.length > 0 &&
          followAction(FollowAction.UnFollow, FollowType.Section, grouped.sectionsId),
        grouped.topicsId.length > 0 &&
          followAction(FollowAction.UnFollow, FollowType.Topic, grouped.topicsId),
      ]);

      onClose?.();
    } catch (error) {
      console.error("Failed to unfollow items:", error);
    }
  };

  return (
    <Container className={className}>
      <Header>
        <Title>MANAGE</Title>
        <IconClose onClick={onClose} />
      </Header>
      <List>
        {followedList.map(item => (
          <ListItem $isSelected={!selectedItems.includes(item.entityId)} key={item.entityId}>
            <ItemType>{item.__typename}</ItemType>
            <ItemName>{item.name}</ItemName>
            <ItemIcon onClick={() => handleItemClick(item.entityId)}>
              {selectedItems.includes(item.entityId) ? <IconFollow /> : <IconUnfollow />}
            </ItemIcon>
          </ListItem>
        ))}
      </List>
      <ButtonContainer>
        <Button onClick={onConfirm}>Confirm</Button>
      </ButtonContainer>
    </Container>
  );
};

TopicFilteringPopup.displayName = "TopicFilteringPopup";
